import { useEffect, useState } from "react";
import { Loader2, LocateFixed, MapPin, Star } from "lucide-react";
import { toast } from "sonner";
import { reverseGeocode, useGeoSearch } from "@/hooks/queries";
import { useDebounced } from "@/hooks/useBrowser";
import { PRESETS } from "@/lib/booking";

function Suggestion({ loc, onChoose, testId }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onChoose({ address: loc.address, lat: loc.lat, lng: loc.lng })}
      data-testid={testId}
      className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-elevated"
    >
      {loc.label ? <Star className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> : <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-mist" />}
      {loc.label ? (
        <span className="min-w-0">
          <span className="block text-ivory">{loc.label}</span>
          <span className="block truncate text-xs text-faint">{loc.address}</span>
        </span>
      ) : (
        <span className="line-clamp-2 text-mist">{loc.address}</span>
      )}
    </button>
  );
}

export function LocationField({ kind, label, value, onChange, active, onFocus }) {
  const [query, setQuery] = useState(value?.address || "");
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  useEffect(() => setQuery(value?.address || ""), [value?.address]);

  const debounced = useDebounced(query.trim());
  const searching = open && debounced.length >= 3 && debounced !== value?.address;
  const { data: results = [], isFetching, isError } = useGeoSearch(searching ? debounced : "");

  const choose = (loc) => {
    onChange(loc);
    setOpen(false);
  };

  const locate = () => {
    onFocus();
    if (!navigator.geolocation) return toast.error("Location isn't available on this device");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        onChange(await reverseGeocode(coords.latitude, coords.longitude));
        setLocating(false);
      },
      () => {
        setLocating(false);
        toast.error("We couldn't access your location. Please search or drop a pin.");
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const list = searching ? results : PRESETS;
  return (
    <div className="relative">
      <div className={`flex items-center gap-3 rounded-2xl border bg-elevated/60 px-4 py-3 transition-colors duration-200 ${active ? "border-gold/60" : "border-line"}`}>
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${kind === "pickup" ? "bg-gold" : "border-2 border-gold bg-ivory"}`} />
        <div className="min-w-0 flex-1">
          <label htmlFor={`${kind}-input`} className="eyebrow block text-[10px]">{label}</label>
          <input
            id={`${kind}-input`}
            data-testid={`${kind}-input`}
            value={query}
            autoComplete="off"
            placeholder="Search an address or venue"
            onFocus={() => {
              onFocus();
              setOpen(true);
            }}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            className="mt-0.5 w-full truncate bg-transparent text-sm text-ivory placeholder:text-faint focus:outline-none"
          />
        </div>
        {isFetching && <Loader2 className="h-4 w-4 animate-spin text-mist" />}
        <button
          type="button"
          onClick={locate}
          title="Use my location"
          data-testid={`${kind}-use-location-btn`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line text-gold transition-colors hover:border-gold/50"
        >
          {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
        </button>
      </div>
      {open && (
        <div data-testid={`${kind}-suggestions`} className="absolute z-[1000] mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-line bg-surface p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
          {!searching && <p className="eyebrow px-3 py-2 text-[10px]">Atlanta favourites</p>}
          {list.map((loc, i) => (
            <Suggestion key={`${loc.lat}-${loc.lng}-${i}`} loc={loc} onChoose={choose} testId={`${kind}-suggestion-${i}`} />
          ))}
          {searching && !isFetching && results.length === 0 && (
            <p className="px-3 py-3 text-sm text-faint" data-testid={`${kind}-no-results`}>
              {isError ? "Address search is unavailable — drop a pin on the map instead." : "No matches yet. Try a fuller address or drop a pin."}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
